import { useQuery } from 'react-query';
import { axios } from '@/lib/axios';
import { Spinner } from '@/components/Elements/Spinner';

type VimeoFile = {
  quality: string;
  rendition: string;
  type: string;
  link: string;
  link_secure: string;
};

type VimeoResponse = {
  success: boolean;
  data: VimeoFile[];
};

const QUALITY_PREFERENCE = ['720p', '1080p', '540p', '480p', '360p', '240p'];

function pickBestUrl(files: VimeoFile[]): string | null {
  for (const rendition of QUALITY_PREFERENCE) {
    const match = files.find(f => f.rendition === rendition && f.link_secure);
    if (match) return match.link_secure;
  }
  return files[0]?.link_secure ?? files[0]?.link ?? null;
}

type Props = {
  vimeoId: string;
  className?: string;
};

export const VimeoPlayer = ({ vimeoId, className }: Props) => {
  const { data, isLoading, error } = useQuery(
    ['vimeo-video', vimeoId],
    async () => {
      const result = (await axios.get(`/vimeo/${vimeoId}`)) as VimeoResponse;
      return result;
    },
    { enabled: !!vimeoId, staleTime: 60 * 60 * 1000 }
  );

  if (isLoading) {
    return (
      <div className="w-full h-48 flex justify-center items-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !data?.data?.length) {
    return (
      <div className="w-full h-48 flex justify-center items-center text-sm text-gray-500">
        Video unavailable
      </div>
    );
  }

  const videoUrl = pickBestUrl(data.data);

  if (!videoUrl) {
    return (
      <div className="w-full h-48 flex justify-center items-center text-sm text-gray-500">
        Video unavailable
      </div>
    );
  }

  return (
    <video
      className={className}
      src={videoUrl}
      controls
      playsInline
      style={{ width: '100%' }}
    />
  );
};
